import { z } from 'zod'
import { NO_ETA_REASONS, etaConfidenceLabelSchema, kestrelFreshnessSchema, kestrelMatchSchema } from './eta.js'

// Board ETAs: POST /api/v1/eta/jobs answers a row per requested job number.

export const MAX_BOARD_JOBS = 2000
const MAX_RAW_JOB_NUMBERS = 10_000
const MAX_JOB_NUMBER_LENGTH = 64

const cleanJobNumbers = (raw: string[]): string[] => [
  ...new Set(raw.map((jobNumber) => jobNumber.trim()).filter((jobNumber) => jobNumber !== '')),
]

export const boardEtaRequestSchema = z.object({
  jobNumbers: z
    .array(z.string().max(MAX_JOB_NUMBER_LENGTH))
    .max(MAX_RAW_JOB_NUMBERS)
    .transform(cleanJobNumbers)
    .pipe(z.array(z.string()).min(1).max(MAX_BOARD_JOBS)),
})
export type BoardEtaRequest = z.infer<typeof boardEtaRequestSchema>

/** Why a board row's Kestrel ETA is missing: the EGIS reasons plus two of Kestrel's own. */
export const KESTREL_BOARD_NO_ETA_REASONS = [...NO_ETA_REASONS, 'Kestrel unavailable', 'No Kestrel officer'] as const

/** The same job worked out from Kestrel's position, beside the EGIS columns while the board comparison is on. */
export const boardEtaKestrelSchema = z.object({
  match: kestrelMatchSchema.nullable(),
  verified: z.boolean(),
  lastSeen: z.string().nullable(),
  freshness: kestrelFreshnessSchema.nullable(),
  accuracyM: z.number().nullable(),
  gapMetres: z.number().nullable(),
  eta: z.string().nullable(),
  driveMinutes: z.number().nullable(),
  driveMiles: z.number().nullable(),
  etaComputedAt: z.string().nullable(),
  sharedWithEgis: z.boolean(),
  confidence: etaConfidenceLabelSchema.nullable(),
  confidenceScore: z.number().nullable(),
  noEtaReason: z.enum(KESTREL_BOARD_NO_ETA_REASONS).nullable(),
})
export type BoardEtaKestrel = z.infer<typeof boardEtaKestrelSchema>

/** One job on the board. Empty values are null, never ''. `kestrel` is absent while the board comparison is off. */
export const boardEtaRowSchema = z.object({
  jobNumber: z.string(),
  executionStatus: z.string().nullable(),
  officerName: z.string().nullable(),
  partnerName: z.string().nullable(),
  lastSeen: z.string().nullable(),
  eta: z.string().nullable(),
  driveMinutes: z.number().nullable(),
  driveMiles: z.number().nullable(),
  etaComputedAt: z.string().nullable(),
  confidence: etaConfidenceLabelSchema.nullable(),
  confidenceScore: z.number().nullable(),
  noEtaReason: z.enum(NO_ETA_REASONS).nullable(),
  checkedAt: z.string(),
  kestrel: boardEtaKestrelSchema.optional(),
})
export type BoardEtaRow = z.infer<typeof boardEtaRowSchema>

export const boardEtaResponseSchema = z.object({
  generatedAt: z.string(),
  jobs: z.array(boardEtaRowSchema),
})
export type BoardEtaResponse = z.infer<typeof boardEtaResponseSchema>
